import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Modal, TextInput } from 'flowbite-react';
import { CheckIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useApi } from '../../../api/useApi.hook';
import { ErrorSwitch } from '../../../components/errorMessage/errorSwitch.component';
import PasswordInput from '../../../components/passwordInput/passwordInput.component';

type AuthMode = 'magicLink' | 'password';

interface MagicLinkForm { email: string }
interface PasswordForm { email: string; password: string }

type Props = {
    onClose?: () => void;
}

const LoginModal = ({}: Props) => {
    const api = useApi();
    const [mode, setMode] = useState<AuthMode>('magicLink');
    const [wasLinkSent, setWasLinkSent] = useState(false);

    const { data: providers } = useQuery({
        queryKey: ['auth-providers'],
        queryFn: () => api.v1.auth.getProviders(),
    });
    const hasGoogle = providers?.some(p => p.providerName === 'google') ?? false;
    const hasPassword = providers?.some(p => p.providerName === 'password') ?? false;

    const doGoogleLogin = useMutation({
        mutationFn: () => api.v1.auth.getGoogleLoginUrl(),
        onSuccess: ({ authUrl }) => { window.location.href = authUrl; },
    });

    const magicForm = useForm<MagicLinkForm>({ defaultValues: { email: '' } });
    const passForm = useForm<PasswordForm>({ defaultValues: { email: '', password: '' } });

    const doMagicLink = useMutation({
        mutationFn: (email: string) => api.v1.auth.provider.magicLink.sendMagicLink(email),
        onSuccess: () => setWasLinkSent(true),
    });

    const doPasswordLogin = useMutation({
        mutationFn: ({ email, password }: PasswordForm) =>
            api.v1.auth.provider.password.login(email, password),
        onSuccess: async (data) => {
            await api.client.establishSession(data.tokens, {
                revokeOldSession: (sessionId) => api.v1.identities.revokeMySession(sessionId)
            });
        },
    });

    const resetState = useCallback(() => setWasLinkSent(false), []);

    useQuery({
        queryKey: ['my-identity-check'],
        queryFn: async () => api.v1.identities.getMyIdentity(),
    });

    return (
        <Modal size='md' show>
            <Modal.Header>Přihlásit se</Modal.Header>
            <Modal.Body className='flex flex-col gap-4'>
                <p className='text-sm text-gray-600'>Vaše přihlášení vypršelo. Pro pokračování se prosím přihlašte znovu.</p>

                {hasGoogle && (
                    <button
                        type='button'
                        onClick={() => doGoogleLogin.mutate()}
                        disabled={doGoogleLogin.isPending}
                        className='flex items-center justify-center gap-3 w-full py-2 px-4 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60'
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                            <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                            <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
                        </svg>
                        {doGoogleLogin.isPending ? 'Přesměrovávám...' : 'Přihlásit se přes Google'}
                    </button>
                )}

                {hasPassword && (
                    <div className='flex gap-2'>
                        <button
                            onClick={() => { setMode('magicLink'); setWasLinkSent(false); }}
                            className={`flex-1 py-1.5 text-sm rounded border ${mode === 'magicLink' ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-600'}`}
                        >
                            Bez hesla
                        </button>
                        <button
                            onClick={() => setMode('password')}
                            className={`flex-1 py-1.5 text-sm rounded border ${mode === 'password' ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-600'}`}
                        >
                            Email a heslo
                        </button>
                    </div>
                )}

                {mode === 'password' && (
                    <form onSubmit={passForm.handleSubmit((d) => doPasswordLogin.mutate(d))} className='flex flex-col gap-4'>
                        <div>
                            <TextInput
                                id="modal-email-pass"
                                type="email"
                                placeholder="E-mail"
                                {...passForm.register('email', { required: 'E-mail je povinný' })}
                                color={passForm.formState.errors.email ? 'failure' : ''}
                            />
                            {passForm.formState.errors.email && (
                                <p className="text-sm text-red-500 mt-1">{passForm.formState.errors.email.message}</p>
                            )}
                        </div>
                        <div>
                            <PasswordInput
                                id="modal-password"
                                placeholder="Heslo"
                                {...passForm.register('password', { required: 'Heslo je povinné' })}
                                color={passForm.formState.errors.password ? 'failure' : ''}
                            />
                            {passForm.formState.errors.password && (
                                <p className="text-sm text-red-500 mt-1">{passForm.formState.errors.password.message}</p>
                            )}
                        </div>
                        <ErrorSwitch
                            className='text-center'
                            error={doPasswordLogin.error}
                            options={[
                                { reasonCode: 'AccountDisabled', message: 'Váš účet byl deaktivován.' },
                                { reasonCode: 'invalid_credentials', message: 'Nesprávný e-mail nebo heslo.' },
                                { reasonCode: 'account_locked', message: 'Účet je dočasně zablokovaný. Zkuste to prosím za chvíli.' },
                            ]}
                            default={{ message: 'Nastala neočekávaná chyba. Zkus to prosím za chvíli znovu.' }}
                        />
                        <Button type="submit" className="w-full" color="dark" disabled={doPasswordLogin.isPending}>
                            {doPasswordLogin.isPending ? 'Přihlašuji...' : 'Přihlásit se'}
                        </Button>
                    </form>
                )}

                {mode === 'magicLink' && (
                    <form onSubmit={magicForm.handleSubmit((d) => doMagicLink.mutate(d.email.trim()))} className='flex flex-col gap-4'>
                        {wasLinkSent ? (
                            <div className='flex flex-col items-center'>
                                <CheckIcon size={48} className='text-lime-700 mb-2' />
                                <h4 className="font-medium text-center mb-4">Prihlašovací odkaz byl odeslán na vámi zadaný e-mail.</h4>
                                <div className='w-full border-t border-t-gray-200 mb-4' />
                                <p className='text-center text-sm mb-4'>Pokud odkaz nedorazil, zkontrolujte prosím složku Spam. Případně se zkuste <button onClick={resetState} className='hyperlink'>přihlásit znovu.</button></p>
                                <p className='text-center text-sm font-semibold'>Po přihlášení se tento dialog automaticky zavře.</p>
                            </div>
                        ) : (
                            <>
                                <TextInput
                                    id="modal-email-magic"
                                    placeholder="Zadejte svůj e-mail"
                                    {...magicForm.register('email', { required: 'E-mail je povinný', pattern: { value: /\S+@\S+\.\S+/, message: 'Zadali jste špatný formát e-mailu' } })}
                                    color={magicForm.formState.errors.email ? 'failure' : ''}
                                />
                                {magicForm.formState.errors.email && (
                                    <p className="text-sm text-red-500 mt-1">{magicForm.formState.errors.email.message}</p>
                                )}
                                <ErrorSwitch
                                    className='text-center'
                                    error={doMagicLink.error}
                                    options={[
                                        { reasonCode: 'AccountDisabled', message: 'Váš účet byl deaktivován. Kontaktujte prosím podporu.' }
                                    ]}
                                    default={{ message: 'Nastala neočekávaná chyba. Zkus to prosím za chvíli znovu.' }}
                                />
                                <Button type="submit" className="w-full" color="dark" disabled={doMagicLink.isPending}>
                                    {doMagicLink.isPending ? 'Přihlašuji...' : 'Přihlásit se'}
                                </Button>
                                <p className="text-sm text-center text-gray-600">Na zadaný e-mail Vám zašleme odkaz pro přihlášení.</p>
                            </>
                        )}
                    </form>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default LoginModal;
